import View            from '@widesk-ui/components/View';
import useDesignTokens from '@/hooks/useDesignTokens';
import React           from 'react';

/** Ligne "radio" réutilisée par `AreaActionSection`/`AreaUrlSection` pour un choix exclusif parmi
 * quelques options (action au clic, mode d'ouverture d'un lien...).
 *
 * `hint` est optionnel À DESSEIN : une explication sous chaque option se lirait comme une paraphrase
 * du libellé et se ferait sauter. On ne la met que là où le libellé ne peut pas tout dire — un terme
 * du produit qu'on rencontre peut-être ici pour la première fois, ou une conséquence invisible. */
export default function Choice({ selected, disabled, badge, hint, onClick, children }: {
	selected?: boolean;
	disabled?: boolean;
	badge?: string;
	hint?: React.ReactNode;
	onClick?: () => void;
	children: React.ReactNode;
}) {
	const tokens = useDesignTokens();

	return (
		<View
			row
			// Avec un `hint`, le point se cale sur la PREMIÈRE ligne : centré, il tomberait entre le
			// libellé et son explication, en face de rien.
			centerV={!hint}
			top={!!hint}
			gap="xs"
			p="sm"
			br={8}
			pointer={!disabled}
			onClick={disabled ? undefined : onClick}
			bg={selected ? `color-mix(in srgb, ${tokens.color.primary} 6%, ${tokens.surface.elevated})` : tokens.surface.elevated}
			hover={disabled || selected ? undefined : { bg: tokens.fill.tertiary }}
			style={{
				border: `1px solid ${selected ? tokens.color.primary : tokens.border.default}`,
				opacity: disabled ? .65 : 1,
			}}
		>
			<View
				w={16}
				h={16}
				br={16}
				center
				style={{
					border: `1.5px solid ${selected ? tokens.color.primary : tokens.text.muted}`,
					flexShrink: 0,
					// Recentre le point sur la hauteur de ligne du libellé (13.5px en 1.5), et non sur son
					// sommet.
					marginTop: hint ? 2 : undefined,
				}}
			>
				{selected && <View w={8} h={8} br={8} bg={tokens.color.primary} />}
			</View>

			<View flex gap={2} style={{ minWidth: 0 }}>
				<View size={14}>{children}</View>

				{!!hint && (
					<View size={12} color={tokens.text.muted} lineHeight={1.4}>
						{hint}
					</View>
				)}
			</View>

			{badge && (
				<View
					size={11}
					px="xs"
					br={100}
					color={tokens.text.muted}
					style={{ background: tokens.surface.card, border: `1px solid ${tokens.border.default}`, letterSpacing: '.03em' }}
				>
					{badge}
				</View>
			)}
		</View>
	);
}
